/**
 * Mini-syntaxe pour le texte des articles : `**gras**` et `[libellé](lien)`.
 *
 * Un seul analyseur sert au rendu React (BlogPostPage), au HTML pré-rendu (scripts/seo-build.mjs),
 * au texte brut des données structurées et au contrôle des liens internes : les quatre voient
 * exactement les mêmes liens.
 */
import { escapeHtml } from "./html.js";

const TOKEN = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;

/** Découpe un texte en jetons `{ type: "text" | "strong" | "link", text, href? }`. */
export function parseInline(input) {
  const source = String(input ?? "");
  const tokens = [];
  let last = 0;
  TOKEN.lastIndex = 0;
  let match = TOKEN.exec(source);
  while (match) {
    if (match.index > last) tokens.push({ type: "text", text: source.slice(last, match.index) });
    if (match[1] !== undefined) tokens.push({ type: "strong", text: match[1] });
    else tokens.push({ type: "link", text: match[2], href: match[3] });
    last = match.index + match[0].length;
    match = TOKEN.exec(source);
  }
  if (last < source.length) tokens.push({ type: "text", text: source.slice(last) });
  return tokens;
}

/** Lien du site (navigation interne) par opposition à un lien sortant. */
export function isInternalHref(href) {
  return typeof href === "string" && href.startsWith("/") && !href.startsWith("//");
}

/** Texte brut (sans balisage) : méta-descriptions, JSON-LD, comptage de mots, llms.txt. */
export function inlineToText(input) {
  return parseInline(input)
    .map((t) => t.text)
    .join("");
}

/** Fragment HTML échappé, pour le contenu pré-rendu. */
export function inlineToHtml(input) {
  return parseInline(input)
    .map((t) => {
      if (t.type === "strong") return `<strong>${escapeHtml(t.text)}</strong>`;
      if (t.type === "link") {
        const external = isInternalHref(t.href) ? "" : ' rel="noopener noreferrer"';
        return `<a href="${escapeHtml(t.href)}"${external}>${escapeHtml(t.text)}</a>`;
      }
      return escapeHtml(t.text);
    })
    .join("");
}

/** Version Markdown (llms-full.txt) : la syntaxe source est déjà du Markdown valide. */
export function inlineToMarkdown(input, baseUrl = "") {
  return parseInline(input)
    .map((t) => {
      if (t.type === "strong") return `**${t.text}**`;
      if (t.type === "link") return `[${t.text}](${isInternalHref(t.href) ? baseUrl + t.href : t.href})`;
      return t.text;
    })
    .join("");
}
